import { useMemo, useState, type ReactNode } from "react";
import { describeError } from "../api/http";
import {
  useAppState,
  useMarket,
  usePlan,
  useSettings,
  useUpdatePlan,
} from "../api/queries";
import type { Listing, MarketFilters, MarketSort } from "../api/types";
import { Page } from "../components/layout/Page";
import {
  PlayerList,
  type PlayerCardData,
  type PlayerColumn,
} from "../components/PlayerCard";
import { Button, ButtonLink } from "../components/ui/Button";
import { ToggleChip } from "../components/ui/Chip";
import { Banner, EmptyState, ErrorState } from "../components/ui/ErrorState";
import { Sheet } from "../components/ui/Sheet";
import { Skeleton } from "../components/ui/Skeleton";
import { count, decimal, haVencido, money, restante } from "../lib/format";
import {
  addToPlan,
  planSideOf,
  removeFromPlan,
  toPlanDraft,
  type PlanDraft,
  type PlanSide,
} from "../lib/plan";
import { POSITIONS, POSITION_NAMES, type Position } from "../lib/positions";

/**
 * Pantalla *Mercado* — M8-T03 (UC-08.03).
 *
 * QUÉ ENSEÑA
 *   Lo que está a la venta ahora mismo: precio de listado, quién lo vende —un participante o el
 *   *Sistema*—, cuánto queda hasta que cierre, las métricas del jugador y la acción de plan. Y,
 *   arriba, el dinero con el que cuento **contando el plan**, que es la cifra con la que se decide
 *   si un fichaje cabe o no.
 *
 * POR QUÉ EL ORDEN Y LOS FILTROS VIAJAN EN LA URL
 *   Al contrario que `GET /api/squad`, `GET /api/market` sí acepta `position`, `maxPrice` y `sort`
 *   (sección 9.1), y el mercado puede traer cientos de ventas: filtrar y ordenar en SQLite con un
 *   índice es lo que ahorra la batería del móvil. La contrapartida es que cada toque de filtro es
 *   una petición nueva, y de ahí la primera línea de la aceptación: `useMarket` lleva
 *   `placeholderData: keepPreviousData` de M7-T02, así que la lista anterior **sigue en pantalla**,
 *   atenuada, hasta que llegan los datos nuevos. No se pinta esqueleto y no se vacía la lista, y
 *   por eso **la posición de scroll no se pierde**: el documento nunca encoge bajo los pies.
 *
 * DE DÓNDE SALE EL PRESUPUESTO
 *   De `GET /api/state`, que trae el dinero de hoy (`budget`) y el que quedaría con el plan
 *   ejecutado (`plan.netBudget`, la cifra que la sección 7.5 llama presupuesto tras el plan). No
 *   hace falta pedir la plantilla entera para eso. `useUpdatePlan` invalida `state` en cuanto el
 *   plan cambia, así que **añadir o quitar una compra mueve la cifra sin recargar**, que es el
 *   segundo punto de la aceptación.
 *
 * LA ACCIÓN DE PLAN MIRA EL PLAN, NO SOLO `inPlan`
 *   Igual que en *Plantilla*: `PUT /api/plan` manda el plan **entero**, así que la acción está
 *   deshabilitada hasta que `GET /api/plan` responde —guardar sin haberlo leído borraría lo que ya
 *   hubiera dentro— y el lado se decide con `planSideOf`, no con el campo `inPlan` de la fila. Ese
 *   campo solo sabe de compras; un jugador que además esté en mis ventas se vería mal.
 *
 * LO EXPIRADO NO SE OCULTA: SE APAGA
 *   Una venta cuyo `until` ya pasó se atenúa **y** lo dice por escrito, y pierde su botón: añadir
 *   al plan una compra que ya no se puede hacer sería prometer algo falso. Se sigue viendo porque
 *   el precio al que se cerró es información: dice por dónde anda el mercado.
 *
 * UNA TARJETA POR JUGADOR, AUNQUE HAYA DOS VENTAS
 *   `Listing` (sección 9.2) no tiene identificador propio: su `id` es el **del jugador**. Y el
 *   servidor puede devolver dos ventas del mismo —una subasta del sistema y la de un
 *   participante—, así que dos filas indistinguibles competirían por la misma ficha. Se agrupan
 *   por jugador y se enseña **la más barata**, que es la que se compraría, diciendo cuántas hay.
 *
 * DÓNDE SE APARTA DEL MOCKUP
 *   `docs/mockups/mercado.html` dibuja los filtros y el orden en **una sola** tira que se
 *   desplaza. Aquí son dos tiras dentro de la misma barra pegajosa: once chips en una sola fila
 *   obligan a rebuscar en horizontal para cambiar de criterio y mezclan dos cosas distintas
 *   —*qué* se ve y en *qué orden*— sin ninguna separación. Cada tira es un `role="group"` con su
 *   nombre, y el `overflow-x` sigue viviendo en ellas y nunca en la página (sección 8.1).
 */

/* =========================================================================== vocabulario == */

/** El chip que no filtra por posición. No es una posición, así que no vive en `lib/positions`. */
const TODAS = "todas";
type FiltroPosicion = Position | typeof TODAS;

/** Los cinco criterios de orden de la sección 9.1, con el texto exacto que se ve en el chip. */
export const MARKET_SORT_LABELS: readonly { id: MarketSort; label: string }[] =
  [
    { id: "score", label: "Recomendados" },
    { id: "expected", label: "Puntos esperados" },
    { id: "efficiency", label: "Eficiencia" },
    { id: "trend", label: "Tendencia" },
    { id: "price", label: "Precio" },
  ];

/**
 * Tope del control deslizante de precio máximo: 30 M, como el mockup. El jugador más caro del
 * catálogo real ronda los 25 M, así que el recorrido cubre el mercado entero con holgura; para lo
 * que se saliera de ahí está *Sin tope*, que quita el filtro en vez de subirlo.
 */
export const TOPE_DEL_DESLIZADOR = 30_000_000;

/** El salto del deslizador: medio millón, que es el grano con el que se mueven los precios. */
const PASO_DEL_DESLIZADOR = 500_000;

/** Quién vende, en palabras. Nunca un identificador numérico (sección 8.3). */
export function vendedor(listing: Listing): string {
  if (listing.source === "system") return "Sistema";
  return listing.sellerName ?? "Otro participante";
}

/** `true` si el plazo de la venta ya pasó. Sin `until` no hay plazo que vencer. */
export function haExpirado(listing: Listing, ahora: number): boolean {
  return haVencido(listing.until, ahora);
}

/** Las ventas abiertas de un jugador: la que se enseña y cuántas hay en total. */
export interface VentaAgrupada {
  /** La más barata de las suyas: es la que se compraría. */
  listing: Listing;
  total: number;
}

/**
 * Una entrada por jugador, en el orden en que llegaron del servidor —que es el que pidió el
 * criterio de orden—, quedándose con la venta más barata de cada uno. No muta la lista que
 * recibe, que es la de la caché de TanStack Query.
 */
export function agruparVentas(listings: readonly Listing[]): VentaAgrupada[] {
  const porJugador = new Map<number, VentaAgrupada>();

  for (const listing of listings) {
    const previa = porJugador.get(listing.id);
    if (previa === undefined) {
      porJugador.set(listing.id, { listing, total: 1 });
      continue;
    }
    porJugador.set(listing.id, {
      listing:
        listing.listingPrice < previa.listing.listingPrice
          ? listing
          : previa.listing,
      total: previa.total + 1,
    });
  }

  return [...porJugador.values()];
}

/* =============================================================================== piezas === */

/**
 * Etiqueta *Cláusula*: ese jugador es de otro equipo y solo se puede fichar pagando su cláusula
 * de rescisión (sección 7.1). Va escrita, no como color: el borde solo acompaña.
 *
 * La ficha de jugador (M8-T04) la importa de aquí en vez de escribir la suya: es vocabulario del
 * mercado, y dos copias acabarían diciendo lo mismo de dos formas distintas.
 */
export function EtiquetaClausula() {
  return (
    <span className="inline-flex h-5 shrink-0 items-center rounded-full border border-money/40 bg-money/10 px-[7px] text-[11px] text-money">
      Cláusula
    </span>
  );
}

/** El plazo de la venta: `18 h` mientras corre, *Expirado* cuando ya no. */
function Plazo({ listing, ahora }: { listing: Listing; ahora: number }) {
  if (listing.until === null) return null;
  if (haExpirado(listing, ahora)) {
    // El color no puede ser la única señal de que la venta ha cerrado: también se escribe.
    return <span className="font-semibold text-down">Expirado</span>;
  }
  const queda = restante(listing.until, ahora);
  if (queda === null) return null;
  return (
    <span className="font-mono tabular-nums">
      {/* «18 h» a secas no dice de qué: para quien no ve la tarjeta, la frase entera. */}
      <span className="sr-only">Quedan </span>
      <span>{queda}</span>
    </span>
  );
}

interface DeslizadorDePrecioProps {
  /** Euros elegidos ahora mismo en el panel, antes de aplicar. */
  value: number;
  onChange: (euros: number) => void;
}

/**
 * El precio máximo se elige con un control deslizante dentro del `Sheet`, no con un campo suelto:
 * es el patrón que pide la sección 8.1 para el móvil y deja la mano donde ya está.
 *
 * `aria-valuetext` es lo que convierte un «doce millones» en algo que el lector de pantalla pueda
 * decir: sin él anunciaría «12000000», que no es una cifra que nadie lea en voz alta.
 */
function DeslizadorDePrecio({ value, onChange }: DeslizadorDePrecioProps) {
  return (
    <div className="grid gap-1">
      <label
        htmlFor="precio-maximo"
        className="flex items-baseline justify-between text-xs text-muted"
      >
        <span>Hasta</span>
        <span className="font-mono text-sm tabular-nums text-money">
          {money(value)}
        </span>
      </label>
      <input
        id="precio-maximo"
        type="range"
        min={0}
        max={TOPE_DEL_DESLIZADOR}
        step={PASO_DEL_DESLIZADOR}
        value={value}
        aria-valuetext={money(value)}
        onChange={(evento) => {
          onChange(Number(evento.target.value));
        }}
        className="h-touch w-full cursor-pointer accent-pitch"
      />
    </div>
  );
}

/** El reparto que ocupará la pantalla mientras llega el mercado (sección 8.2). */
function EsqueletoDelMercado() {
  return (
    <>
      <Skeleton variant="text" className="w-3/4" label="Cargando" />
      <Skeleton variant="card" count={5} label="Cargando el mercado" />
    </>
  );
}

/* ============================================================================= pantalla === */

export function Market() {
  const [posicion, setPosicion] = useState<FiltroPosicion>(TODAS);
  const [sort, setSort] = useState<MarketSort>("score");
  /** `null` = sin tope. Es el filtro aplicado, no lo que se está moviendo en el panel. */
  const [precioMaximo, setPrecioMaximo] = useState<number | null>(null);
  const [panelAbierto, setPanelAbierto] = useState(false);
  const [borradorDePrecio, setBorradorDePrecio] = useState(TOPE_DEL_DESLIZADOR);

  const filtros: MarketFilters = useMemo(
    () => ({
      position: posicion === TODAS ? undefined : posicion,
      maxPrice: precioMaximo ?? undefined,
      sort,
    }),
    [posicion, precioMaximo, sort],
  );

  const mercado = useMarket(filtros);
  const estado = useAppState();
  const ajustes = useSettings();
  const plan = usePlan();
  const guardarPlan = useUpdatePlan();

  const listados = mercado.data?.listings ?? [];
  const hayListados = listados.length > 0;
  const hayFiltros = posicion !== TODAS || precioMaximo !== null;

  /** Una entrada por jugador: dos ventas del mismo no pueden ser dos fichas iguales. */
  const ventas = useMemo(() => agruparVentas(listados), [listados]);

  /**
   * El reloj de la pantalla se lee **una vez por pintado**. No hay temporizador propio: el mercado
   * se refresca solo al volver a la pestaña y cada 30 s de `staleTime` (sección 8.2), y cada
   * respuesta vuelve a pintar con la hora de ese momento. Un cronómetro por venta daría un segundo
   * de precisión a un dato que se mide en horas.
   */
  const ahora = Date.now();

  /** La venta detrás de una fila de `PlayerList`, que solo recibe lo que pinta. */
  const porId = useMemo(
    () => new Map(ventas.map((venta) => [venta.listing.id, venta])),
    [ventas],
  );

  function ventaDe(player: PlayerCardData): VentaAgrupada | null {
    return porId.get(player.id) ?? null;
  }

  function listadoDe(player: PlayerCardData): Listing | null {
    return ventaDe(player)?.listing ?? null;
  }

  const borrador: PlanDraft = toPlanDraft(plan.data);
  const planLeido = plan.data !== undefined;

  /**
   * En qué lado del plan está el jugador. `GET /api/plan` es la fuente; mientras no responde se
   * usa `inPlan`, que basta para marcar la tarjeta aunque no sirva para editar.
   */
  function ladoEnPlan(listing: Listing | null): PlanSide | null {
    if (listing === null) return null;
    if (!planLeido) return listing.inPlan === "buy" ? "buys" : null;
    return planSideOf(borrador, listing.id);
  }

  /**
   * Las fichas que pinta `PlayerCard`. El **precio de la venta** sustituye al del catálogo: es lo
   * que hay que pagar, y es la cifra con la que se compara el presupuesto. El del catálogo sigue
   * a un toque de distancia, en la ficha del jugador.
   */
  const fichas: PlayerCardData[] = useMemo(
    () =>
      ventas.map(({ listing }) => ({
        id: listing.id,
        name: listing.name,
        position: listing.position,
        team: listing.team,
        price: listing.listingPrice,
        priceIncrement: listing.priceIncrement,
        status: listing.status,
        statusText: listing.statusText,
        metrics: listing.metrics,
      })),
    [ventas],
  );

  /**
   * Qué se dice del precio de una cláusula. Si coincide con `precio × league.clauseMarkup` es que
   * el payload de Biwenger no traía la cláusula y el servidor la estimó (sección 7.1): eso hay que
   * decirlo, porque la cifra real puede ser otra.
   */
  function notaDeClausula(listing: Listing): string {
    const markup = ajustes.data?.league.clauseMarkup;
    if (markup === undefined)
      return "Solo se puede fichar pagando su cláusula.";
    const estimado = Math.round(listing.price * markup);
    if (estimado !== listing.listingPrice) {
      return "Solo se puede fichar pagando su cláusula.";
    }
    return `Precio de cláusula estimado (×${decimal(markup, 1)}): la cifra real puede variar.`;
  }

  function accionDePlan(listing: Listing): ReactNode {
    // Una venta cerrada ya no se puede fichar: no se ofrece la acción, se explica por qué.
    if (haExpirado(listing, ahora)) return null;

    const lado = ladoEnPlan(listing);
    const etiqueta = lado === null ? "Añadir al plan" : "Quitar del plan";

    return (
      <Button
        size="sm"
        variant={lado === null ? "primary" : "secondary"}
        disabled={!planLeido || guardarPlan.isPending}
        // Muchos botones con el mismo texto: el nombre distingue de cuál es cada uno.
        aria-label={`${etiqueta}: ${listing.name}`}
        onClick={() => {
          guardarPlan.mutate(
            lado === null
              ? addToPlan(borrador, "buys", {
                  playerId: listing.id,
                  price: listing.listingPrice,
                })
              : removeFromPlan(borrador, lado, listing.id),
          );
        }}
      >
        {etiqueta}
      </Button>
    );
  }

  /** La columna propia de esta pantalla en la tabla de ≥ 768 px: quién vende y hasta cuándo. */
  const columnaVenta: PlayerColumn = {
    header: "Venta",
    render: (player) => {
      const listado = listadoDe(player);
      if (listado === null) return null;
      return (
        <span className="grid justify-items-end gap-0.5">
          <span className="font-sans">{vendedor(listado)}</span>
          <Plazo listing={listado} ahora={ahora} />
        </span>
      );
    },
  };

  const presupuestoConPlan = estado.data?.plan.netBudget ?? null;
  const presupuestoHoy = estado.data?.budget ?? null;

  /* --------------------------------------------------------------- la barra de filtros - */

  function abrirPanelDePrecio(): void {
    setBorradorDePrecio(precioMaximo ?? TOPE_DEL_DESLIZADOR);
    setPanelAbierto(true);
  }

  /**
   * La barra desaparece solo cuando no hay nada que filtrar **ni** ningún filtro puesto. Si un
   * filtro deja la lista vacía tiene que seguir ahí: esconderla sería encerrar al usuario en un
   * mercado vacío sin manera de deshacerlo.
   */
  const barraDeFiltros =
    mercado.isPending || hayListados || hayFiltros ? (
      // Una sola barra pegajosa con dos tiras: el `overflow-x` vive en cada tira, nunca en la
      // página (sección 8.1). Ninguna de las dos se desmonta al cambiar de filtro, así que su
      // desplazamiento horizontal —y el de la página— se quedan donde estaban.
      <div className="sticky top-[52px] z-10 grid gap-1.5 border-b border-border bg-paper px-3.5 py-2 md:px-[18px]">
        <div
          role="group"
          aria-label="Filtrar el mercado"
          className="flex gap-2 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        >
          <ToggleChip
            pressed={posicion === TODAS}
            onClick={() => {
              setPosicion(TODAS);
            }}
          >
            Todas
          </ToggleChip>
          {POSITIONS.map((una) => (
            <ToggleChip
              key={una}
              pressed={posicion === una}
              title={POSITION_NAMES[una]}
              onClick={() => {
                setPosicion(una);
              }}
            >
              {una}
            </ToggleChip>
          ))}
          <ToggleChip
            pressed={precioMaximo !== null}
            aria-haspopup="dialog"
            aria-expanded={panelAbierto}
            onClick={abrirPanelDePrecio}
          >
            {precioMaximo === null
              ? "Precio máximo"
              : `≤ ${money(precioMaximo)}`}
          </ToggleChip>
        </div>

        <div
          role="group"
          aria-label="Ordenar el mercado por"
          className="flex gap-2 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        >
          {MARKET_SORT_LABELS.map((criterio) => (
            <ToggleChip
              key={criterio.id}
              pressed={sort === criterio.id}
              onClick={() => {
                setSort(criterio.id);
              }}
            >
              {criterio.label}
            </ToggleChip>
          ))}
        </div>
      </div>
    ) : undefined;

  /* -------------------------------------------------------------------------- el cuerpo - */

  let cuerpo: ReactNode;
  if (mercado.isPending) {
    cuerpo = <EsqueletoDelMercado />;
  } else if (mercado.data === undefined) {
    cuerpo = (
      <ErrorState
        title="No se ha podido cargar el mercado"
        message={describeError(mercado.error)}
        onRetry={() => void mercado.refetch()}
      />
    );
  } else if (!hayListados) {
    // Un mercado vacío por culpa de los filtros no se arregla sincronizando: se arregla
    // quitando los filtros. Decir «Sincroniza» aquí sería mandar al usuario al sitio contrario.
    cuerpo = hayFiltros ? (
      <EmptyState
        title="Ninguna venta con estos filtros"
        message="Prueba con otra posición o sube el precio máximo."
        action={
          <Button
            variant="primary"
            onClick={() => {
              setPosicion(TODAS);
              setPrecioMaximo(null);
            }}
          >
            Quitar los filtros
          </Button>
        }
      />
    ) : (
      <EmptyState
        title="El mercado está vacío"
        message="Sincroniza desde Inicio para ver qué hay a la venta."
        action={
          <ButtonLink to="/" variant="primary">
            Ir a Inicio
          </ButtonLink>
        }
      />
    );
  } else {
    cuerpo = (
      <PlayerList
        label="Ventas del mercado"
        players={fichas}
        // Datos anteriores mientras llegan los nuevos: la lista se atenúa, no se vacía.
        stale={mercado.isPlaceholderData}
        column={columnaVenta}
        state={(player) => {
          const listado = listadoDe(player);
          if (listado === null) return "default";
          if (haExpirado(listado, ahora)) return "expired";
          return ladoEnPlan(listado) === null ? "default" : "inPlan";
        }}
        extra={(player) => {
          const venta = ventaDe(player);
          if (venta === null) return null;
          const listado = venta.listing;
          const eficiencia = listado.metrics?.efficiency;
          return (
            <>
              <span>{vendedor(listado)}</span>
              <Plazo listing={listado} ahora={ahora} />
              {venta.total > 1 ? (
                <span>{count(venta.total)} ventas abiertas</span>
              ) : null}
              {eficiencia === undefined ? null : (
                <span className="font-mono tabular-nums">
                  Efic. {decimal(eficiencia)}
                </span>
              )}
              {listado.requiresClause ? <EtiquetaClausula /> : null}
              {/* El borde de acento no puede ser la única señal del plan. */}
              {ladoEnPlan(listado) === "buys" ? (
                <span className="font-semibold text-pitch">Compra en plan</span>
              ) : null}
            </>
          );
        }}
        actions={(player) => {
          const listado = listadoDe(player);
          if (listado === null) return null;
          const boton = accionDePlan(listado);
          const nota = listado.requiresClause ? (
            <p className="w-full text-xs text-muted">
              {notaDeClausula(listado)}
            </p>
          ) : null;
          // Sin botón ni nota no se devuelve un envoltorio vacío: dejaría un hueco muerto.
          if (boton === null && nota === null) return null;
          return (
            <>
              {boton}
              {nota}
            </>
          );
        }}
      />
    );
  }

  return (
    <Page
      title="Mercado"
      sticky={barraDeFiltros}
      action={
        presupuestoConPlan === null ? undefined : (
          <span className="font-mono text-[13px] tabular-nums text-money">
            <span className="sr-only">Presupuesto con el plan: </span>
            <span>{money(presupuestoConPlan)}</span>
          </span>
        )
      }
    >
      <p className="text-xs text-muted">
        {presupuestoConPlan === null ? (
          "Cargando el presupuesto…"
        ) : (
          <>
            Presupuesto disponible con el plan aplicado:{" "}
            <span className="font-mono tabular-nums text-money">
              {money(presupuestoConPlan)}
            </span>
            {presupuestoHoy !== null &&
            presupuestoHoy !== presupuestoConPlan ? (
              <> (hoy: {money(presupuestoHoy)})</>
            ) : null}
          </>
        )}
        {mercado.data === undefined
          ? null
          : ` · ${count(listados.length)} ${
              listados.length === 1 ? "venta" : "ventas"
            }`}
      </p>

      {estado.isError ? (
        <ErrorState
          title="No se ha podido leer el presupuesto"
          message={describeError(estado.error)}
          onRetry={() => void estado.refetch()}
        />
      ) : null}

      {plan.isError ? (
        <ErrorState
          title="No se ha podido leer el plan"
          message={describeError(plan.error)}
          onRetry={() => void plan.refetch()}
        />
      ) : null}

      {plan.data !== undefined && !plan.data.planValid ? (
        <Banner title="El plan no se sostiene">
          <ul className="grid list-disc gap-1 pl-5">
            {plan.data.reasons.map((motivo) => (
              <li key={motivo}>{motivo}</li>
            ))}
          </ul>
        </Banner>
      ) : null}

      {guardarPlan.isError ? (
        <Banner tone="error" title="No se ha podido guardar el plan">
          {describeError(guardarPlan.error)}
        </Banner>
      ) : null}

      {cuerpo}

      <Sheet
        open={panelAbierto}
        onClose={() => {
          setPanelAbierto(false);
        }}
        title="Precio máximo"
        footer={
          <>
            <Button
              onClick={() => {
                setPanelAbierto(false);
              }}
            >
              Cancelar
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                setPrecioMaximo(borradorDePrecio);
                setPanelAbierto(false);
              }}
            >
              Aplicar
            </Button>
          </>
        }
      >
        <DeslizadorDePrecio
          value={borradorDePrecio}
          onChange={setBorradorDePrecio}
        />
        {/* Sin esta salida el filtro sería una trampa: bajar el tope se puede, quitarlo no. */}
        <Button
          variant="ghost"
          onClick={() => {
            setPrecioMaximo(null);
            setPanelAbierto(false);
          }}
        >
          Quitar el tope
        </Button>
      </Sheet>
    </Page>
  );
}
